import React, { useState, useCallback, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, Download, Upload, Trash2, PlayCircle, PauseCircle, Settings2, Zap, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db, safeHttpsCallable } from '@/lib/firebase';

// Import client-side API functions
import { 
  setSystemStatus, 
  getSystemStatus,
  performFullAggregation,
  performFullAlgoliaSync,
  getAlgoliaUsage,
  clearAlgoliaIndices
} from '@/lib/api';

// Import from TypeScript file
import {
  exportFirestoreData,
  importFirestoreData,
  initializeFirestore
} from '@/utils/initFirestore';

// Add import for AdminStatusDisplay
import AdminStatusDisplay from '@/components/ui/AdminStatusDisplay';

// Types
type SystemSettingStatus = 'enabled' | 'disabled';
type LogMessage = string;

interface OperationProps {
  label: string;
  action: () => Promise<void>;
  icon?: React.ReactNode;
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  confirmRequired?: boolean;
  confirmTitle?: string;
  confirmDescription?: string;
  disabled?: boolean;
  className?: string;
}

const SESSION_LOGS_KEY = 'soda-tracker-operation-logs';

export const DataManager = () => {
  // Core state
  const [logs, setLogs] = useState<LogMessage[]>(() => {
    try {
      return JSON.parse(sessionStorage.getItem(SESSION_LOGS_KEY) || '[]');
    } catch {
      return [];
    }
  });
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [algoliaStatus, setAlgoliaStatus] = useState<SystemSettingStatus>('enabled');
  const [aggregationStatus, setAggregationStatus] = useState<SystemSettingStatus>('enabled');
  const [, setSystemStatusLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [operationInProgress, setOperationInProgress] = useState(false);
  
  // UI state for import/export
  const [resetBeforeImport, setResetBeforeImport] = useState(false);
  const [useNameBasedFormat, setUseNameBasedFormat] = useState(true);
  const [validationIssues, setValidationIssues] = useState<Array<{severity: 'error' | 'warning'; message: string}>>([]);
  const [showValidationResults, setShowValidationResults] = useState(false);
  const [currentFileContent, setCurrentFileContent] = useState<string | null>(null);

  // Logging function
  const logMessage = useCallback((message: string) => {
    const logEntry = `${new Date().toISOString()} - ${message}`;
    setLogs(prev => {
      const newLogs = [logEntry, ...prev.slice(0, 199)]; // Keep last 200 logs
      try {
        sessionStorage.setItem(SESSION_LOGS_KEY, JSON.stringify(newLogs));
      } catch {} // Ignore storage errors
      return newLogs;
    });
  }, []);

  // Clear logs
  const clearLogs = useCallback(() => {
    setLogs([]);
    try {
      sessionStorage.removeItem(SESSION_LOGS_KEY);
    } catch {} // Ignore storage errors
  }, []);

  // Check admin status
  useEffect(() => {
    const checkAdmin = async () => {
      setIsLoading(true);
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        
        if (!user) {
          setIsAdmin(false);
          return;
        }
        
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          setIsAdmin(true);
          return;
        }
        
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setIsAdmin(userData.role === 'admin' || userData.isAdmin === true);
          } else {
          setIsAdmin(false);
        }
      } catch (error) {
        logMessage(`Error checking admin status: ${error instanceof Error ? error.message : String(error)}`);
        setIsAdmin(false);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAdmin();
  }, [logMessage]);

  // Load system status when admin is confirmed
  const fetchSystemStatus = useCallback(async () => {
    if (isAdmin !== true) return;
    setSystemStatusLoading(true);
    try {
      const status = await getSystemStatus();
      if (status) {
        setAlgoliaStatus(status.algolia?.status || 'disabled');
        setAggregationStatus(status.aggregation?.status || 'disabled');
        logMessage(`Fetched system status: Algolia=${status.algolia?.status}, Aggregation=${status.aggregation?.status}`);
        }
      } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      logMessage(`Failed to load system status: ${msg}`);
      toast.error(`Failed to load system status: ${msg}`);
    } finally {
      setSystemStatusLoading(false);
    }
  }, [isAdmin, logMessage]);

  useEffect(() => {
    fetchSystemStatus();
  }, [fetchSystemStatus]);

  // Update a specific system status (Algolia or Aggregation)
  const updateSpecificSystemStatus = async (type: 'algolia' | 'aggregation', newStatus: SystemSettingStatus) => {
    try {
      const payload: { algoliaStatus?: SystemSettingStatus, aggregationStatus?: SystemSettingStatus } = {};
      if (type === 'algolia') payload.algoliaStatus = newStatus;
      if (type === 'aggregation') payload.aggregationStatus = newStatus;
      
      await setSystemStatus(payload);
      
      if (type === 'algolia') setAlgoliaStatus(newStatus);
      if (type === 'aggregation') setAggregationStatus(newStatus);
      return true;
    } catch (error) {
      console.error(`Error setting ${type} status:`, error);
      return false;
    }
  };
  
  // Refresh Algolia usage stats (called by Operation component)
  const refreshAlgoliaStats = async () => {
    try {
      await getAlgoliaUsage(); 
      logMessage("Algolia usage stats refreshed.");
    } catch (error) {
      logMessage(`Failed to refresh Algolia stats: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  // Generic Operation Component
  const Operation = ({ label, action, icon, variant = "outline", confirmRequired = false, confirmTitle, confirmDescription, disabled = false, className = '' }: OperationProps) => {
    const [isProcessing, setIsProcessing] = useState(false);
    
    const handleAction = async () => {
      if (operationInProgress && !isProcessing) return;
      
      setIsProcessing(true);
      setOperationInProgress(true);
      logMessage(`--- Starting operation: ${label} ---`);
      
      try {
        await action();
        toast.success(`${label} completed successfully.`);
        if (label.toLowerCase().includes('algolia') || label.toLowerCase().includes('reset') || label.toLowerCase().includes('import')) {
          refreshAlgoliaStats();
        }
        fetchSystemStatus();
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        toast.error(`${label} failed: ${message}`);
        logMessage(`Error in ${label}: ${message}`);
    } finally {
        logMessage(`--- Finished operation: ${label} ---`);
        setIsProcessing(false);
        setOperationInProgress(false);
    }
  };

    const buttonContent = (
    <>
        {isProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : icon}
        {isProcessing ? 'Processing...' : label}
    </>
  );

    const isDisabled = disabled || (operationInProgress && !isProcessing);

    if (confirmRequired) {
    return (
      <AlertDialog>
        <AlertDialogTrigger asChild>
            <Button variant={variant} disabled={isDisabled} className={`w-full justify-center ${className}`}>
            {buttonContent}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
              <AlertDialogTitle>{confirmTitle || `Confirm ${label}`}</AlertDialogTitle>
            <AlertDialogDescription>
                {confirmDescription || "Are you sure you want to continue with this operation?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
              <AlertDialogCancel disabled={isProcessing}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleAction} disabled={isProcessing}>
                {isProcessing ? 'Processing...' : 'Continue'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
      <Button 
        variant={variant} 
        onClick={handleAction} 
        disabled={isDisabled} 
        className={`w-full justify-center ${className}`}
      >
      {buttonContent}
    </Button>
  );
};

  // Operation handlers
  const handleFullReset = async () => {
    logMessage("Preparing for full reset...");
    await updateSpecificSystemStatus('algolia', 'disabled');
    await updateSpecificSystemStatus('aggregation', 'disabled');
    logMessage("System statuses disabled. Clearing Algolia...");
    await clearAlgoliaIndices();
    logMessage("Algolia cleared. Initializing Firestore with default data...");
    await initializeFirestore(logMessage);
    logMessage("Full reset completed. Please re-enable system statuses and run full syncs/aggregations if needed.");
  };
  
  const handleExport = async () => {
    logMessage(`Exporting data (name-based format: ${useNameBasedFormat})`);
    const jsonData = await exportFirestoreData(logMessage);
    
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
    a.download = `soda-tracker-export-${useNameBasedFormat ? 'name' : 'id'}-${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    logMessage("Export completed");
  };

  const handleImport = async (fileContent: string) => {
    logMessage(`Preparing for import (reset before import: ${resetBeforeImport}). Disabling system statuses...`);
    await updateSpecificSystemStatus('algolia', 'disabled');
    await updateSpecificSystemStatus('aggregation', 'disabled');
    logMessage("System statuses disabled. Starting data import...");
    await importFirestoreData(fileContent, resetBeforeImport, logMessage);
    logMessage("Import completed. Please re-enable system statuses and run full syncs/aggregations.");
  };

  const handleConfigureAlgolia = async () => {
    logMessage("Configuring Algolia indices");
    await safeHttpsCallable('configureAlgoliaIndices');
    logMessage("Algolia configuration completed");
  };
  
  const handlePerformFullAggregation = async () => {
    logMessage("Starting full aggregation process...");
    await performFullAggregation();
    logMessage("Full aggregation process completed.");
  };

  const handleSyncAlgolia = async () => {
    logMessage("Starting full Algolia sync");
    await performFullAlgoliaSync();
    logMessage("Full Algolia sync completed");
  };

  const handleClearAlgolia = async () => {
    logMessage("Clearing Algolia indices");
    await clearAlgoliaIndices();
    logMessage("Algolia indices cleared");
  };

  const handleToggleAlgoliaStatus = async () => {
    const newStatus = algoliaStatus === 'enabled' ? 'disabled' : 'enabled';
    logMessage(`Attempting to set Algolia status to: ${newStatus}`);
    const success = await updateSpecificSystemStatus('algolia', newStatus);
    if (success) {
      logMessage(`Algolia status changed to ${newStatus}`);
    } else {
      logMessage(`Failed to change Algolia status. It might still be ${algoliaStatus}.`);
      fetchSystemStatus();
    }
  };
  
  const handleToggleAggregationStatus = async () => {
    const newStatus = aggregationStatus === 'enabled' ? 'disabled' : 'enabled';
    logMessage(`Attempting to set Aggregation status to: ${newStatus}`);
    const success = await updateSpecificSystemStatus('aggregation', newStatus);
    if (success) {
      logMessage(`Aggregation status changed to ${newStatus}`);
    } else {
      logMessage(`Failed to change Aggregation status. It might still be ${aggregationStatus}.`);
      fetchSystemStatus();
    }
  };

  if (isLoading) {
  return (
      <div className="flex flex-col h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin mb-4" />
        <p>Loading Data Manager...</p>
      </div>
    );
  }
  
  if (isAdmin === false) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
        <p>You do not have administrator privileges to access this section.</p>
      </div>
    );
  }

  // Export Dialog component
  const ExportDialog = () => (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" className="w-full justify-center sm:w-auto" disabled={operationInProgress}>
          <Download className="mr-2 h-4 w-4" />
          Export Data
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Export Database Data</AlertDialogTitle>
          <AlertDialogDescription>
            Export the Soda Track database to a JSON file.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="grid gap-4 py-4">
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="useNameBasedFormat" 
              checked={useNameBasedFormat} 
              onCheckedChange={(checked) => setUseNameBasedFormat(checked === true)} 
            />
            <Label htmlFor="useNameBasedFormat" className="text-sm font-medium">
              Use name-based references (recommended for readability)
            </Label>
          </div>
          </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Operation
              label="Confirm Export"
              action={handleExport}
              variant="default"
            />
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  // Import Dialog component
  const ImportDialog = () => {
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    
    const triggerImportOperation = async (): Promise<void> => {
      if (!fileInputRef.current?.files?.length) {
        toast.error("Please select a file to import");
        return Promise.reject(new Error("No file selected"));
      }
      
      try {
        const file = fileInputRef.current.files[0];
        const fileContent = await file.text();
        
        // Store the file content in state for later use
        setCurrentFileContent(fileContent);
        
        // Log start of validation process
        logMessage("Beginning import validation...");
        
        try {
          const { validateImportData } = await import('@/utils/firestore/validation');
          logMessage("Validation module loaded, starting validation...");
          
          // Try parsing the JSON first to identify potential JSON errors
          try {
            JSON.parse(fileContent);
            logMessage("JSON parsing successful");
          } catch (jsonError) {
            const errorMsg = jsonError instanceof Error ? jsonError.message : String(jsonError);
            logMessage(`ERROR: JSON parsing failed: ${errorMsg}`);
            return Promise.reject(new Error(`Import file contains invalid JSON: ${errorMsg}`));
          }
          
          // Now run the actual validation
          const validationResult = validateImportData(fileContent);
          setValidationIssues(validationResult.issues);
          
          logMessage(`Validation complete: ${validationResult.issues.length} issues found (${validationResult.issues.filter(i => i.severity === 'error').length} errors, ${validationResult.issues.filter(i => i.severity === 'warning').length} warnings)`);
          
          if (validationResult.issues.length > 0) {
            setShowValidationResults(true);
            if (!validationResult.valid) {
              logMessage("Import validation failed with errors.");
              return Promise.reject(new Error("Import validation failed"));
            }
            logMessage("Import validation has warnings. User review required.");
            // Still reject, as user needs to review warnings via the second dialog
            return Promise.reject(new Error("Validation warnings, user review pending")); 
          }
          
          // If no issues, proceed to actual import
          logMessage("Validation successful, proceeding to import...");
          await handleImport(fileContent);
          // Clear the stored file content after successful import
          setCurrentFileContent(null);
          // toast.success is handled by the Operation component wrapper for this path
        } catch (error) {
          // Enhanced error reporting for validation errors
          const errorMessage = error instanceof Error ? error.message : String(error);
          logMessage(`ERROR in validation/import process: ${errorMessage}`);
          
          // Try to provide more context
          if (errorMessage.includes("Cannot read properties of null (reading 'name')")) {
            logMessage("This appears to be a null reference error. Check if your data contains null objects or properties in key collections.");
            // Log a sample of the file content (first 500 chars) to help debug
            try {
              const sampleContent = fileContent.slice(0, 500) + "...";
              logMessage(`File content sample: ${sampleContent}`);
            } catch (e) {
              logMessage("Could not retrieve file content sample.");
            }
          }
          
          return Promise.reject(error); // Re-throw for Operation component to catch
        }
      } catch (outerError) {
        const errorMessage = outerError instanceof Error ? outerError.message : String(outerError);
        logMessage(`CRITICAL ERROR in import process: ${errorMessage}`);
        return Promise.reject(outerError);
      }
    };

    const continueImportAfterValidation = async (): Promise<void> => {
      setShowValidationResults(false);
      if (validationIssues.some(issue => issue.severity === 'error')) {
        toast.error("Cannot import data with validation errors");
        logMessage("Attempted to continue import with errors. Blocked.");
        return Promise.reject(new Error("Validation errors exist"));
      }
      
      // Check if we have the file content stored in state
      if (!currentFileContent) {
        toast.error("File content lost. Please select the file again.");
        logMessage("File content lost during validation pause.");
        return Promise.reject(new Error("File content lost"));
      }
      
      try {
        await handleImport(currentFileContent); 
        // Clear the stored file content after successful import
        setCurrentFileContent(null);
        // toast.success handled by Operation wrapper
        logMessage("Import continued and completed after validation review.");
      } catch (error) {
        // toast.error handled by Operation wrapper
        logMessage(`Error continuing import after validation: ${error instanceof Error ? error.message : String(error)}`);
        return Promise.reject(error);
      }
    };
  
    return (
      <>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="w-full justify-center sm:w-auto" disabled={operationInProgress}>
              <Upload className="mr-2 h-4 w-4" />
              Import Data
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Import Database Data</AlertDialogTitle>
              <AlertDialogDescription>
                Import a previously exported Soda Track database file. This operation will temporarily disable Aggregation and Algolia sync.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-1">
                <Label htmlFor="importFile">Import File (.json)</Label>
                <input
                  id="importFile"
                  type="file"
                  ref={fileInputRef}
                  accept=".json"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={operationInProgress}
                />
              </div>
              <div 
                className="flex items-center space-x-2"
                // Adding stopPropagation to prevent checkbox interaction from closing dialog
                onClick={(e) => e.stopPropagation()} 
                onKeyDown={(e) => {
                  // Allow spacebar to toggle checkbox without closing dialog
                  if (e.key === ' ') {
                    e.stopPropagation();
                  }
                }}
              >
                <Checkbox 
                  id="resetBeforeImport" 
                  checked={resetBeforeImport} 
                  onCheckedChange={(checked) => {
                    setResetBeforeImport(checked === true);
                  }} 
                  disabled={operationInProgress}
                  aria-labelledby="resetBeforeImportLabel"
                />
                <Label 
                  htmlFor="resetBeforeImport" 
                  className="text-sm font-medium cursor-pointer"
                  id="resetBeforeImportLabel"
                >
                  Reset database before import
                </Label>
              </div>
              <p className="text-xs text-muted-foreground">
                Note: Aggregation and Algolia sync will be temporarily disabled during the import process. They should be re-enabled and processes run manually afterwards if needed.
              </p>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={operationInProgress}>Cancel</AlertDialogCancel>
              <Operation 
                label="Confirm Import"
                action={triggerImportOperation} 
                variant="default"
                // The Operation component itself will be disabled if operationInProgress is true
                // and its own isProcessing state isn't true.
              />
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      
        <AlertDialog open={showValidationResults} onOpenChange={(open) => {
          setShowValidationResults(open);
          if (!open) {
            logMessage("Import validation dialog closed.");
          }
        }}>
          <AlertDialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
            <AlertDialogHeader>
              <AlertDialogTitle>
                Import Validation Results
                {validationIssues.length > 0 && (
                  <div className="flex gap-2 mt-2">
                    <Badge variant={validationIssues.filter(i => i.severity === 'error').length > 0 ? "destructive" : "default"}>
                      {validationIssues.filter(i => i.severity === 'error').length} Errors
                    </Badge>
                    <Badge variant="secondary">
                      {validationIssues.filter(i => i.severity === 'warning').length} Warnings
                    </Badge>
                  </div>
                )}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {validationIssues.some(i => i.severity === 'error')
                  ? "The import data contains errors that must be fixed before importing."
                  : validationIssues.length > 0
                    ? "The import data contains warnings. Review them carefully before continuing. Continuing with warnings might lead to unexpected data states."
                    : "The import data appears valid and is ready to import."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="overflow-auto flex-1 my-4 border rounded-md bg-muted/20">
              {validationIssues.length === 0 ? (
                <div className="p-4 text-center text-sm text-green-600 dark:text-green-400">No validation issues found.</div>
              ) : (
                <div className="divide-y divide-border">
                  {validationIssues.map((issue, index) => (
                    <div key={index} className={`p-3 flex items-start gap-2 ${issue.severity === 'error' ? 'bg-red-50 dark:bg-red-900/10' : 'bg-yellow-50 dark:bg-yellow-800/10'}`}>
                      <div className={`text-xs font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 ${issue.severity === 'error' ? 'text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30' : 'text-yellow-700 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30'}`}>
                        {issue.severity}
                      </div>
                      <div className="text-sm text-foreground/90">{issue.message}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => logMessage("Import cancelled from validation screen.")}>
                Cancel Import
              </AlertDialogCancel>
              {!validationIssues.some(i => i.severity === 'error') && validationIssues.length > 0 && (
                <Operation
                  label="Continue With Warnings"
                  action={continueImportAfterValidation}
                  variant="default" // Or perhaps a warning variant if available/desired
                  confirmRequired // Good idea to confirm if there are warnings
                  confirmTitle="Confirm Import with Warnings"
                  confirmDescription="You are about to import data with warnings. Are you sure you want to proceed? This could lead to unexpected data states."
                />
              )}
              {/* If no errors and no warnings (or if we allow continuing with only errors, which is bad) */}
              {/* This case is covered by the triggerImportOperation if validation passed initially */}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  };

  return (
    <div className="flex flex-col h-full overflow-auto p-4 space-y-4">
      <AdminStatusDisplay variant="full" />
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between flex-wrap gap-2">
            <span>Data Management Dashboard</span>
          </CardTitle>
          <CardDescription>
            Manage database operations, system statuses, and Algolia search integration.
          </CardDescription>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader><CardTitle>Core Database Operations</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Operation
            label="Full Data Reset"
            action={handleFullReset}
            icon={<Trash2 className="mr-2 h-4 w-4" />}
            variant="destructive"
            confirmRequired={true}
            confirmTitle="Confirm Full Data Reset"
            confirmDescription="This will ERASE ALL Firestore data and replace it with initial defaults. Algolia will be cleared. This action is irreversible."
          />
          <ExportDialog />
          <ImportDialog />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle>Aggregation Control</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <Operation
              label={aggregationStatus === 'enabled' ? "Disable Aggregation" : "Enable Aggregation"}
              action={handleToggleAggregationStatus}
              icon={aggregationStatus === 'enabled' ? <PauseCircle className="mr-2 h-4 w-4" /> : <PlayCircle className="mr-2 h-4 w-4" />}
              variant={aggregationStatus === 'enabled' ? "destructive" : "default"}
            />
            <Operation
              label="Run Full Aggregation"
              action={handlePerformFullAggregation}
              icon={<Zap className="mr-2 h-4 w-4" />}
              confirmRequired
              confirmTitle="Confirm Full Aggregation"
              confirmDescription="This will recalculate all aggregated data. It may take some time and temporarily disable triggers."
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Algolia Search Control</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <Operation
              label={algoliaStatus === 'enabled' ? "Disable Algolia Sync" : "Enable Algolia Sync"}
              action={handleToggleAlgoliaStatus}
              icon={algoliaStatus === 'enabled' ? <PauseCircle className="mr-2 h-4 w-4" /> : <PlayCircle className="mr-2 h-4 w-4" />}
              variant={algoliaStatus === 'enabled' ? "destructive" : "default"}
            />
            <Operation
              label="Run Full Algolia Sync"
              action={handleSyncAlgolia}
              icon={<RefreshCw className="mr-2 h-4 w-4" />}
              confirmRequired
              confirmTitle="Confirm Full Algolia Sync"
              confirmDescription="This will re-sync all relevant data to Algolia. It may take some time."
            />
            <Operation
              label="Configure Algolia Indices"
              action={handleConfigureAlgolia}
              icon={<Settings2 className="mr-2 h-4 w-4" />}
            />
            <Operation
              label="Clear Algolia Indices"
              action={handleClearAlgolia}
              icon={<Trash2 className="mr-2 h-4 w-4" />}
              variant="destructive"
              confirmRequired={true}
              confirmTitle="Confirm Clear Algolia Indices"
              confirmDescription="This will delete ALL data from Algolia search indices. Firestore data remains. This is irreversible."
            />
          </CardContent>
        </Card>

        {import.meta.env.DEV && (
          <Card>
            <CardHeader><CardTitle>Development Testing</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <Button 
                variant="outline" 
                onClick={async () => {
                  try {
                    logMessage("Testing Cloud Functions connection...");
                    // Use the safeHttpsCallable wrapper from firebase.ts
                    const result = await safeHttpsCallable('getSystemStatus');
                    logMessage(`Function call succeeded with data: ${JSON.stringify(result.data)}`);
                    toast.success("Cloud Functions call succeeded!");
                  } catch (error: any) {
                    logMessage(`Function call failed: ${error.message}`);
                    console.error("Cloud Functions call error:", error);
                    toast.error(`Function call failed: ${error.message}`);
                  }
                }}
                className="w-full"
              >
                Test Cloud Functions Connection
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
          
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Operation Logs</CardTitle>
          <Button variant="ghost" size="sm" onClick={clearLogs} title="Clear logs" disabled={operationInProgress || logs.length === 0}>
            <Trash2 className="h-4 w-4 mr-1" /> Clear Logs
              </Button>
        </CardHeader>
        <CardContent className="h-[300px] md:h-[400px]">
          <div className="border rounded-md bg-background h-full overflow-auto p-2">
            <pre className="whitespace-pre-wrap break-all text-xs">
                {logs.length > 0 ? logs.join('\n') : "No operations performed yet."}
              </pre>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};